import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BrandIdentity from "../../componants/common/BrandIdentity";
import { useBrand } from "../../context/BrandContext";
import { useIndustryLabels } from "../../context/IndustryContext";
import {
  deletePayslipLogo,
  fetchPayslipLogo,
  getMyPayslips,
  uploadPayslipLogo,
} from "../../api/payroll";
import "./MyPayslip.css";

const MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const STATUS_LABEL = {
  APPROVED: "معتمد",
  PAID: "تم الصرف",
};

function getApiErrorMessage(error, fallback) {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return error?.message || fallback;
}

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("ar-EG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDays(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  if (Number.isInteger(num)) return String(num);
  return num.toLocaleString("ar-EG", { maximumFractionDigits: 2 });
}

function monthLabel(month, year, weekNumber) {
  const name = MONTHS[(Number(month) || 1) - 1] || month;
  if (Number(weekNumber) > 0) {
    return `${name} ${year} — الأسبوع ${Number(weekNumber)}`;
  }
  return `${name} ${year}`;
}

function Line({ label, value, total = false }) {
  return (
    <div className={`payslip-line${total ? " is-total" : ""}`}>
      <span>{label}</span>
      <strong>{formatMoney(value)}</strong>
    </div>
  );
}

export default function MyPayslip({ canManageLogo = false }) {
  const brand = useBrand();
  const industryLabels = useIndustryLabels();
  const [slips, setSlips] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [hasSecondLogo, setHasSecondLogo] = useState(false);
  const [secondLogoUrl, setSecondLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [logoBusy, setLogoBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const logoRequest = useRef(0);

  const loadLogo = useCallback(async (enabled) => {
    const requestId = logoRequest.current + 1;
    logoRequest.current = requestId;
    if (!enabled) {
      setSecondLogoUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return "";
      });
      return;
    }
    const blob = await fetchPayslipLogo();
    if (logoRequest.current !== requestId) return;
    const nextUrl = blob ? URL.createObjectURL(blob) : "";
    if (logoRequest.current !== requestId) {
      if (nextUrl) URL.revokeObjectURL(nextUrl);
      return;
    }
    setSecondLogoUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return nextUrl;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getMyPayslips();
      const list = Array.isArray(payload?.slips)
        ? payload.slips
        : Array.isArray(payload)
          ? payload
          : [];
      const logoFlag = Boolean(payload?.hasSecondLogo);
      setSlips(list);
      setHasSecondLogo(logoFlag);
      setSelectedId((current) => {
        if (current && list.some((slip) => slip.id === current)) return current;
        return list[0]?.id || "";
      });
      await loadLogo(logoFlag);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل كشف الراتب"));
      setSlips([]);
    } finally {
      setLoading(false);
    }
  }, [loadLogo]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (secondLogoUrl) URL.revokeObjectURL(secondLogoUrl);
    };
  }, [secondLogoUrl]);

  const slip = useMemo(
    () => slips.find((item) => item.id === selectedId) || slips[0] || null,
    [slips, selectedId],
  );

  async function onUploadLogo(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    setNotice("");
    setError("");
    try {
      await uploadPayslipLogo(file);
      setHasSecondLogo(true);
      await loadLogo(true);
      setNotice("تم تحديث الشعار الثاني");
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر رفع الشعار"));
    } finally {
      setLogoBusy(false);
    }
  }

  async function onRemoveLogo() {
    setLogoBusy(true);
    setNotice("");
    setError("");
    try {
      await deletePayslipLogo();
      setHasSecondLogo(false);
      await loadLogo(false);
      setNotice("تمت إزالة الشعار الثاني");
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إزالة الشعار"));
    } finally {
      setLogoBusy(false);
    }
  }

  return (
    <div className="payslip-page">
      <div className="payslip-toolbar">
        <div>
          <h1>كشف الراتب</h1>
          <p>للاطلاع فقط — يظهر بعد اعتماد الموارد البشرية</p>
        </div>
        <div className="payslip-toolbar-actions">
          {slips.length > 1 && (
            <label className="payslip-month">
              الشهر
              <select
                value={slip?.id || ""}
                onChange={(event) => setSelectedId(event.target.value)}
              >
                {slips.map((item) => (
                  <option key={item.id} value={item.id}>
                    {monthLabel(item.month, item.year, item.weekNumber)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            className="payslip-print"
            onClick={() => window.print()}
            disabled={!slip}
          >
            طباعة
          </button>
        </div>
      </div>

      {canManageLogo && (
        <section className="payslip-logo-admin">
          <div>
            <strong>الشعار الثاني</strong>
            <p>يظهر بجانب شعار التطبيق. يمكن للموارد البشرية استبداله.</p>
          </div>
          <div className="payslip-logo-admin-actions">
            <label className={`payslip-upload${logoBusy ? " is-busy" : ""}`}>
              {logoBusy
                ? "جارٍ الحفظ..."
                : hasSecondLogo
                  ? "استبدال الشعار"
                  : "إضافة شعار"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onUploadLogo}
                disabled={logoBusy}
              />
            </label>
            {hasSecondLogo && (
              <button type="button" onClick={onRemoveLogo} disabled={logoBusy}>
                إزالة
              </button>
            )}
          </div>
        </section>
      )}

      {error && <div className="payslip-banner is-error">{error}</div>}
      {notice && <div className="payslip-banner is-ok">{notice}</div>}

      {loading ? (
        <div className="payslip-empty">جارٍ تحميل الكشف...</div>
      ) : !slip ? (
        <div className="payslip-empty">
          لا يوجد كشف راتب معتمد بعد. يظهر هنا بعد اعتماد الموارد البشرية.
        </div>
      ) : (
        <article className="payslip-card">
          <div className="payslip-watermark" aria-hidden="true">
            للاطلاع فقط
          </div>
          <header
            className={`payslip-head${
              secondLogoUrl || canManageLogo ? "" : " is-single-logo"
            }`}
          >
            <div className="payslip-logo-slot">
              <BrandIdentity
                showName={false}
                logoClassName="payslip-brand-logo"
                fallbackClassName="payslip-brand-fallback"
              />
            </div>
            <div className="payslip-head-copy">
              <p className="payslip-kicker">{brand.companyName}</p>
              <h2>كشف الراتب</h2>
              <p>{monthLabel(slip.month, slip.year, slip.weekNumber)}</p>
            </div>
            <div className="payslip-logo-slot">
              {secondLogoUrl ? (
                <img src={secondLogoUrl} alt="شعار الشركة" className="payslip-brand-logo" />
              ) : (
                <span className="payslip-logo-placeholder">أضف شعار</span>
              )}
            </div>
          </header>

          <div className="payslip-badges">
            <span>للاطلاع فقط</span>
            <span>{STATUS_LABEL[slip.status] || slip.status}</span>
          </div>

          <dl className="payslip-identity">
            <div>
              <dt>اسم الموظف</dt>
              <dd>{slip.employeeName || "—"}</dd>
            </div>
            <div>
              <dt>المسمى الوظيفي</dt>
              <dd>{slip.jobTitle || "—"}</dd>
            </div>
            <div>
              <dt>الكود</dt>
              <dd>{slip.employeeCode || "—"}</dd>
            </div>
            <div>
              <dt>الدوام</dt>
              <dd>{slip.shiftLabel || "—"}</dd>
            </div>
            <div>
              <dt>الفرع</dt>
              <dd>{slip.branchName || "—"}</dd>
            </div>
          </dl>

          <div className="payslip-stats">
            <div>
              <span>أيام الحضور</span>
              <strong>{formatDays(slip.attendanceDays)}</strong>
            </div>
            <div>
              <span>صافي أيام العمل</span>
              <strong>{formatDays(slip.netWorkingDays)}</strong>
            </div>
            <div>
              <span>أيام الإجازة</span>
              <strong>{formatDays(slip.leaveDays)}</strong>
            </div>
            <div>
              <span>الجزاءات</span>
              <strong>{formatMoney(slip.penaltyAmount)}</strong>
            </div>
          </div>

          <div className="payslip-columns">
            <section>
              <h3>الاستحقاقات</h3>
              <Line label="الراتب الثابت" value={slip.earnings?.fixedSalary} />
              <Line label="البدلات" value={slip.earnings?.allowances} />
              <Line label="الإضافي" value={slip.earnings?.overtime} />
              <Line
                label={industryLabels.targetPayslip}
                value={slip.earnings?.target}
              />
              <Line label="إضافي نقدي" value={slip.earnings?.cashBonus} />
              <Line label="بدلات انتقال" value={slip.earnings?.travelAllowance} />
              {Number(slip.earnings?.other) > 0 && (
                <Line label="بنود أخرى" value={slip.earnings.other} />
              )}
              <Line label="إجمالي الاستحقاقات" value={slip.earnings?.total} total />
            </section>
            <section className="is-deduction">
              <h3>الاستقطاعات</h3>
              <Line label="أيام خارج التعاقد" value={slip.deductions?.outsideContractDays} />
              <Line label="غياب بدون إذن" value={slip.deductions?.unauthorizedAbsence} />
              <Line label="أذونات إضافية" value={slip.deductions?.extraPermits} />
              <Line label="الجزاءات" value={slip.deductions?.penalties} />
              <Line label="السلف" value={slip.deductions?.advances} />
              <Line label="العجز" value={slip.deductions?.deficit} />
              {Number(slip.deductions?.other) > 0 && (
                <Line label="استقطاعات أخرى" value={slip.deductions.other} />
              )}
              <Line label="إجمالي الاستقطاعات" value={slip.deductions?.total} total />
            </section>
          </div>

          <footer className="payslip-net">
            <span>صافي الراتب المستحق</span>
            <strong>
              {formatMoney(slip.netSalary)}
              <small>ج.م</small>
            </strong>
          </footer>
        </article>
      )}
    </div>
  );
}
